import jsPDF from 'jspdf';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_dental-quote-builder/artifacts/1jzjglja_1000x310%20JT_AZUL_02.png';

// Function to load image as base64
const loadImageAsBase64 = async (url) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error loading logo:', error);
    return null;
  }
};

export const generatePDF = async ({ patient, items, customItems, totals, discountPercent, financingMonths, exchangeRate, currency, notes }) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const contentWidth = pageWidth - (margin * 2);
  
  // Colors - Updated to match JT Ads blue
  const brandBlue = [0, 112, 243]; // JT Ads blue
  const accentOrange = [194, 65, 12];
  const textDark = [12, 10, 9];
  const textMuted = [87, 83, 78];
  
  let y = margin;

  // Helper functions
  const drawLine = (yPos, color = [231, 229, 228]) => {
    doc.setDrawColor(...color);
    doc.setLineWidth(0.3);
    doc.line(margin, yPos, pageWidth - margin, yPos);
  };

  const formatPrice = (usd, showBoth = false) => {
    const cop = usd * exchangeRate;
    if (showBoth) {
      return `$${usd.toLocaleString('en-US', { maximumFractionDigits: 0 })} USD / $${cop.toLocaleString('es-CO', { maximumFractionDigits: 0 })} COP`;
    }
    if (currency === 'USD') {
      return `$${usd.toLocaleString('en-US', { maximumFractionDigits: 0 })} USD`;
    }
    return `$${cop.toLocaleString('es-CO', { maximumFractionDigits: 0 })} COP`;
  };

  const checkPageBreak = (requiredSpace) => {
    if (y + requiredSpace > 270) {
      doc.addPage();
      y = margin;
      return true;
    }
    return false;
  };

  // Header with dark background
  doc.setFillColor(12, 12, 12);
  doc.rect(0, 0, pageWidth, 45, 'F');
  
  // Try to load and add logo
  try {
    const logoBase64 = await loadImageAsBase64(LOGO_URL);
    if (logoBase64) {
      // Logo dimensions: 1000x310, scale to fit header
      doc.addImage(logoBase64, 'PNG', margin, 8, 60, 18.6);
    }
  } catch (e) {
    // Fallback to text if logo fails
    doc.setTextColor(...brandBlue);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('JT ADS', margin, 25);
  }
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Presupuesto de Tratamiento Dental', margin, 38);
  
  // Date
  const date = new Date().toLocaleDateString('es-CO', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  doc.setFontSize(9);
  doc.text(date, pageWidth - margin, 25, { align: 'right' });
  doc.text(`TRM: $${exchangeRate.toLocaleString('es-CO')} COP/USD`, pageWidth - margin, 35, { align: 'right' });

  y = 60;

  // Countries map for PDF
  const countriesMap = {
    "US": "Estados Unidos",
    "CO": "Colombia",
    "MX": "México",
    "ES": "España",
    "AR": "Argentina",
    "CL": "Chile",
    "PE": "Perú",
    "EC": "Ecuador",
    "VE": "Venezuela",
    "BR": "Brasil",
    "CA": "Canadá",
    "PA": "Panamá",
    "CR": "Costa Rica",
    "DO": "República Dominicana",
    "PR": "Puerto Rico",
    "OTHER": "Otro"
  };

  // Patient Information
  doc.setTextColor(...textDark);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('INFORMACIÓN DEL PACIENTE', margin, y);
  y += 8;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...textMuted);
  
  doc.text(`Nombre: ${patient.name}`, margin, y);
  y += 6;
  if (patient.country) {
    doc.text(`País: ${countriesMap[patient.country] || patient.country}`, margin, y);
    y += 6;
  }
  if (patient.phone) {
    doc.text(`Teléfono: ${patient.phone}`, margin, y);
    y += 6;
  }
  if (patient.email) {
    doc.text(`Email: ${patient.email}`, margin, y);
    y += 6;
  }
  
  y += 8;
  drawLine(y);
  y += 12;

  // Group items by viaje
  const groupedByViaje = items.reduce((acc, item) => {
    const viaje = item.viaje || 1;
    if (!acc[viaje]) acc[viaje] = [];
    acc[viaje].push(item);
    return acc;
  }, {});

  // Group custom items by viaje
  const customGroupedByViaje = customItems.reduce((acc, item) => {
    const viaje = item.viaje || 1;
    if (!acc[viaje]) acc[viaje] = [];
    acc[viaje].push(item);
    return acc;
  }, {});

  // Get all unique viajes
  const allViajes = [...new Set([
    ...Object.keys(groupedByViaje).map(Number),
    ...Object.keys(customGroupedByViaje).map(Number)
  ])].sort((a, b) => a - b);

  // Maxilar labels
  const maxilarLabels = {
    superior: 'Maxilar Superior',
    inferior: 'Maxilar Inferior',
    general: 'General'
  };

  // Render each viaje
  for (const viaje of allViajes) {
    checkPageBreak(40);

    // Viaje Header - Updated to JT Ads blue
    doc.setFillColor(0, 112, 243);
    doc.rect(margin, y - 4, contentWidth, 10, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(`VIAJE ${viaje}`, margin + 5, y + 2);
    
    // Calculate viaje total
    const viajeItems = groupedByViaje[viaje] || [];
    const viajeCustom = customGroupedByViaje[viaje] || [];
    const viajeTotal = viajeItems.reduce((sum, item) => sum + (item.price_usd * item.quantity), 0) +
                       viajeCustom.reduce((sum, item) => sum + item.price_usd, 0);
    
    doc.text(formatPrice(viajeTotal), pageWidth - margin - 5, y + 2, { align: 'right' });
    y += 14;

    // Treatment Items for this viaje
    if (viajeItems.length > 0) {
      // Table Header
      doc.setFillColor(245, 245, 244);
      doc.rect(margin, y - 4, contentWidth, 8, 'F');
      
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...textMuted);
      doc.text('TRATAMIENTO', margin + 2, y);
      doc.text('MAXILAR', margin + 80, y);
      doc.text('CANT.', margin + 115, y);
      doc.text('TOTAL', pageWidth - margin - 2, y, { align: 'right' });
      y += 8;

      // Table Rows
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...textDark);
      doc.setFontSize(8);

      for (const item of viajeItems) {
        checkPageBreak(10);
        
        const itemTotal = item.price_usd * item.quantity;
        const maxilarLabel = maxilarLabels[item.maxilar] || 'General';
        
        doc.text(item.treatment_name.substring(0, 40), margin + 2, y, { maxWidth: 75 });
        doc.setTextColor(...textMuted);
        doc.text(maxilarLabel, margin + 80, y);
        doc.setTextColor(...textDark);
        doc.text(item.quantity.toString(), margin + 120, y);
        doc.text(formatPrice(itemTotal), pageWidth - margin - 2, y, { align: 'right' });
        
        y += 7;
        drawLine(y - 2, [240, 240, 240]);
      }
    }

    // Custom Items for this viaje
    if (viajeCustom.length > 0) {
      y += 3;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...textMuted);
      doc.text('SERVICIOS ADICIONALES', margin + 2, y);
      y += 6;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);

      const typeLabels = {
        flight: 'Vuelo',
        hotel: 'Alojamiento',
        transport: 'Transporte',
        other: 'Otro'
      };

      for (const item of viajeCustom) {
        checkPageBreak(10);

        doc.setTextColor(...textMuted);
        doc.text(`[${typeLabels[item.item_type]}]`, margin + 2, y);
        doc.setTextColor(...textDark);
        doc.text(item.name.substring(0, 50), margin + 30, y, { maxWidth: 100 });
        doc.text(formatPrice(item.price_usd), pageWidth - margin - 2, y, { align: 'right' });
        
        y += 7;
      }
    }

    y += 10;
  }

  // Summary Box
  y += 5;
  checkPageBreak(70);

  // Summary Background
  doc.setFillColor(250, 250, 249);
  doc.roundedRect(margin, y, contentWidth, 65, 3, 3, 'F');
  
  y += 10;
  doc.setFontSize(10);
  
  // Subtotal
  doc.setTextColor(...textMuted);
  doc.text('Subtotal', margin + 5, y);
  doc.setTextColor(...textDark);
  doc.setFont('helvetica', 'bold');
  doc.text(formatPrice(totals.subtotal, true), pageWidth - margin - 5, y, { align: 'right' });
  
  y += 8;
  
  // Discount
  if (discountPercent > 0) {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...accentOrange);
    doc.text(`Descuento (${discountPercent}%)`, margin + 5, y);
    doc.text(`-${formatPrice(totals.discountAmount)}`, pageWidth - margin - 5, y, { align: 'right' });
    y += 8;
  }
  
  // Total
  drawLine(y, [200, 200, 200]);
  y += 8;
  
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...brandBlue);
  doc.text('TOTAL', margin + 5, y);
  doc.text(formatPrice(totals.total, true), pageWidth - margin - 5, y, { align: 'right' });
  
  y += 12;
  
  // Financing
  if (financingMonths > 1) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...textMuted);
    doc.text(`${financingMonths} cuotas de`, margin + 5, y);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...brandBlue);
    doc.text(formatPrice(totals.monthlyPayment), pageWidth - margin - 5, y, { align: 'right' });
    y += 10;
  }

  // Notes section
  if (notes && notes.trim()) {
    y += 5;
    checkPageBreak(40);
    
    doc.setFillColor(250, 250, 249);
    doc.roundedRect(margin, y, contentWidth, 35, 3, 3, 'F');
    
    y += 8;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...textDark);
    doc.text('NOTAS Y OBSERVACIONES', margin + 5, y);
    
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...textMuted);
    doc.setFontSize(8);
    
    // Split notes into lines
    const noteLines = doc.splitTextToSize(notes, contentWidth - 10);
    noteLines.slice(0, 4).forEach((line) => {
      doc.text(line, margin + 5, y);
      y += 5;
    });
  }

  // Footer
  const footerY = doc.internal.pageSize.getHeight() - 25;
  
  drawLine(footerY - 5);
  
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...textMuted);
  doc.text('Este presupuesto es válido por 30 días.', margin, footerY);
  doc.text('Los precios están sujetos a valoración presencial.', margin, footerY + 5);
  
  doc.setTextColor(...brandBlue);
  doc.text('Tel: +1 (239) 691-6075', pageWidth - margin, footerY, { align: 'right' });
  doc.text('www.jtads.co', pageWidth - margin, footerY + 5, { align: 'right' });

  // Save
  const fileName = `Presupuesto_${patient.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
  
  return fileName;
};
